import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { ApiService } from './api.service';
import { ReferenceAppServiceModel } from '../models/reference-app-service.model';
import { ReferenceSignatureModel } from '../models/reference-signature.model';
import { ApiShortListModel } from '../models/api-list.model';
import { ReferenceAppServicesService } from './reference-app-services.service';
import { AdUserModel } from '../../views/user-management/models/adUser.model';

@Injectable({
  providedIn: 'root',
})
export class ReferenceSignaturesService {
  private signatureList$ = new BehaviorSubject<ReferenceSignatureModel[]>([]);

  private referenceSignatureImgList = [
    { id: 'SI0035', imgName: 'biotherm', displayImgOnly: false },
    { id: 'SI0054', imgName: 'carita-paris', displayImgOnly: true },
    { id: 'SI0039', imgName: 'giorgio-armani', displayImgOnly: true },
    { id: 'SI0030', imgName: 'helena-rubinstein', displayImgOnly: true },
    { id: 'SI0036', imgName: 'kiehl-s', displayImgOnly: true },
    { id: 'SI0008', imgName: 'l-oreal-professionnel', displayImgOnly: true },
    { id: 'SI0020', imgName: 'lancome-paris', displayImgOnly: false },
    { id: 'SI0124', imgName: 'prada', displayImgOnly: true },
    { id: 'SI0046', imgName: 'skin-ceuticals', displayImgOnly: true },
    { id: 'SI0028', imgName: 'yves-saint-laurent', displayImgOnly: false },
  ];

  constructor(
    public _apiService: ApiService,
    private _referenceAppServicesService: ReferenceAppServicesService
  ) {}

  getList(): Observable<ReferenceSignatureModel[]> {
    return this.signatureList$.pipe(
      switchMap((signatureList: ReferenceSignatureModel[]) =>
        signatureList.length ? of(signatureList) : this.fetchListFromApi()
      )
    );
  }

  private fetchListFromApi(): Observable<ReferenceSignatureModel[]> {
    const referenceAppServiceList$ =
      this._referenceAppServicesService.getList();

    const referenceSignatureList$ = this._apiService.get<
      ApiShortListModel<ReferenceSignatureModel>
    >('reference-signatures');

    return combineLatest([
      referenceAppServiceList$,
      referenceSignatureList$,
    ]).pipe(
      map(
        ([
          referenceAppServiceList,
          referenceSignatureList,
        ]): ReferenceSignatureModel[] => {
          return referenceSignatureList.value.map(
            (referenceSignatureInList: ReferenceSignatureModel) => {
              const referenceSignature: ReferenceSignatureModel = {
                ...referenceSignatureInList,
              };

              const referenceSignatureImg = this.referenceSignatureImgList.find(
                referenceSignatureImgInList =>
                  referenceSignatureImgInList.id === referenceSignature.id
              );

              if (referenceSignatureImg) {
                referenceSignature.imgName = referenceSignatureImg.imgName;
                referenceSignature.displayImgOnly =
                  referenceSignatureImg.displayImgOnly;
              }

              referenceSignature.appServiceList =
                referenceAppServiceList.filter(
                  (referenceAppServiceInList: ReferenceAppServiceModel) =>
                    referenceAppServiceInList.signatureId ===
                    referenceSignature.id
                );

              return referenceSignature;
            }
          );
        }
      ),
      tap(signatureList => this.signatureList$.next(signatureList))
    );
  }

  public filterListForUser(
    signatureList: ReferenceSignatureModel[],
    userInfo?: AdUserModel
  ): ReferenceSignatureModel[] {
    if (userInfo?.isAdminAccount) {
      return signatureList;
    }

    return signatureList
      .map(
        (signatureInList: ReferenceSignatureModel): ReferenceSignatureModel => {
          signatureInList.appServiceList = signatureInList.appServiceList.filter(
            (appService: ReferenceAppServiceModel) => {
              return userInfo?.appServiceList.find(
                (appServiceInList: ReferenceAppServiceModel) => {
                  return appService.id === appServiceInList.id;
                }
              );
            }
          );

          return signatureInList;
        }
      )
      .filter(
        (signatureInList: ReferenceSignatureModel) =>
          signatureInList.appServiceList?.length !== 0
      );
  }

  public getAppServiceInSignatureList(
    referenceSignatureModels: ReferenceSignatureModel[],
    appServiceId: number
  ): ReferenceAppServiceModel | undefined {
    let appServiceFound: ReferenceAppServiceModel | undefined = undefined;

    referenceSignatureModels.forEach(
      (referenceSignature: ReferenceSignatureModel) => {
        if (!appServiceFound) {
          appServiceFound = referenceSignature.appServiceList.find(
            (appServiceInList: ReferenceAppServiceModel) =>
              appServiceInList.id === appServiceId
          );
        }
      }
    );

    return appServiceFound;
  }
}
